import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LanguageService } from '@assethub/shared/services';
import { DatePickerModule } from 'primeng/datepicker';
import { PrimeNG } from 'primeng/config';

type TouchFn = () => void;
type ChangeFn = (val: Date) => void;

@UntilDestroy()
@Component({
  selector: 'app-date-field',
  templateUrl: './date-field.component.html',
  styleUrls: ['./date-field.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DateFieldComponent), multi: true },
  ],
  imports: [DatePickerModule, FormsModule],
})
export class DateFieldComponent implements ControlValueAccessor, OnInit {
  @Input() name: string;
  @Input() label: string;
  @Input() disabled = false;
  @Input() showTime = false;
  @Input() @Optional() appendTo: string | undefined;
  @Output() blurred: EventEmitter<boolean> = new EventEmitter();

  placeholder = '';

  private internalDate: Date;
  private onTouch?: TouchFn;
  private onChange?: ChangeFn;

  get dateValue(): Date {
    return this.internalDate;
  }

  set dateValue(val: Date) {
    this.internalDate = val;
    if (this.onChange) {
      this.onChange(val);
    }
  }

  constructor(
    private config: PrimeNG,
    private languageService: LanguageService,
  ) {}

  ngOnInit(): void {
    // DateFormat of Calendar is
    // d (dd): day of month (with leading zeros)
    // D (DD): name of weekday short (long)
    // m (mm): number of month (with leading zeros)
    // M (MM): name of month short (long)
    // y: last two digits of year
    // yy: full year
    // o (oo): day of year (with leading zeros)
    this.languageService
      .translate('datePicker')
      .pipe(untilDestroyed(this))
      .subscribe(t => {
        this.config.setTranslation(t);
        this.placeholder = t['placeholder'];
        // force update of calendar
        if (this.dateValue) {
          this.writeValue(new Date(this.dateValue));
        }
      });
  }

  writeValue(val: Date): void {
    if (val !== this.dateValue) {
      this.dateValue = val;
    }
  }

  registerOnChange(fn: ChangeFn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: TouchFn): void {
    this.onTouch = fn;
  }

  onBlur(): void {
    if (this.onTouch) {
      this.onTouch();
    }
    this.blurred.emit(true);
  }
}
